// Tables the user takes away as a file: query results on the Query page, a
// class's instances on Browse, a vocabulary on the Vocabulary page.
import type { SparqlBinding } from '@/lib/api';

/**
 * CSV: quote if cell contains separator, quote, newline; double internal quotes.
 * TSV: replace tab/newline with a space — strict TSV has no escape rules,
 * and embedded tabs would corrupt column alignment.
 */
export function escapeCell(text: string, sep: string): string {
  if (sep === ',') {
    if (/[",\n\r]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  }
  return text.replace(/[\t\n\r]+/g, ' ');
}

/** A header and rows as delimited text, one line per row. */
export function toDelimited(header: string[], rows: string[][], sep = ','): string {
  const lines = [header, ...rows].map((row) => row.map((cell) => escapeCell(cell, sep)).join(sep));
  return lines.join('\n') + '\n';
}

/** Where a value in a cell of several values is split. */
export const MULTI_VALUE_SEPARATOR = '; ';

/** The name a column takes from a term: the part after the last `#`, `/` or
 *  `:`, as written. */
function columnName(uri: string): string {
  const idx = Math.max(uri.lastIndexOf('#'), uri.lastIndexOf('/'), uri.lastIndexOf(':'));
  return idx >= 0 && idx < uri.length - 1 ? uri.slice(idx + 1) : uri;
}

/** Every term a class table names: each property and each value that is a
 *  thing, so their labels can be read before the table is built. */
export function instanceTableTerms(bindings: Array<Record<string, SparqlBinding | undefined>>): string[] {
  const terms = new Set<string>();
  for (const b of bindings) {
    if (b.p?.value) terms.add(b.p.value);
    if (b.o?.type === 'uri') terms.add(b.o.value);
  }
  return [...terms];
}

/**
 * One row per instance and one column per property, from rows of
 * (?s ?p ?o): an instance, a property and its value. A property heads its
 * column by its label, and a value that names a thing shows its label, when
 * `labels` has one; a property with several values holds them in one cell,
 * split by "; ". The first column is the instance's identifier. Two
 * properties with one name keep their full identifiers as headers, so no
 * column is ambiguous.
 */
export function instanceTable(
  bindings: Array<Record<string, SparqlBinding | undefined>>,
  labels: ReadonlyMap<string, string> = new Map(),
): { header: string[]; rows: string[][] } {
  const subjects = new Map<string, Map<string, string[]>>();
  const predicates: string[] = [];
  const seenPredicate = new Set<string>();
  for (const b of bindings) {
    const s = b.s?.value;
    const p = b.p?.value;
    const o = b.o;
    if (!s || !p || !o) continue;
    if (!seenPredicate.has(p)) {
      seenPredicate.add(p);
      predicates.push(p);
    }
    let cells = subjects.get(s);
    if (!cells) {
      cells = new Map();
      subjects.set(s, cells);
    }
    const value = o.type === 'uri' ? (labels.get(o.value) ?? o.value) : o.value;
    const values = cells.get(p) ?? [];
    if (!values.includes(value)) values.push(value);
    cells.set(p, values);
  }
  const names = predicates.map((p) => labels.get(p) ?? columnName(p));
  const header = [
    'Identifier',
    ...predicates.map((p, i) => {
      const name = names[i] ?? p;
      return names.indexOf(name) !== names.lastIndexOf(name) ? p : name;
    }),
  ];
  const rows = [...subjects].map(([s, cells]) => [
    s,
    ...predicates.map((p) => (cells.get(p) ?? []).join(MULTI_VALUE_SEPARATOR)),
  ]);
  return { header, rows };
}
